import React from 'react';
import { FileText, Shield, AlertTriangle, Scale, Users, CreditCard, Lock, Globe, MessageSquare, Gavel } from 'lucide-react';
import { useSiteSettings } from '../context/SiteSettingsContext';

const TermsConditions = () => {
  const { settings } = useSiteSettings();
  
  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Hero Section */}
      <div className="bg-[#0A0A0A] text-white py-20 border-b border-[#D4AF37]/20">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <div className="w-20 h-20 bg-[#D4AF37]/20 rounded-full flex items-center justify-center mx-auto mb-6 border border-[#D4AF37]/50">
            <Scale className="h-10 w-10 text-[#D4AF37]" />
          </div>
          <h1 className="text-5xl lg:text-6xl font-bold mb-4 font-heading">Terms & Conditions</h1>
          <p className="text-xl text-[#888888] font-body">Please read these terms carefully before using our services</p>
          <p className="text-[#D4AF37] font-body mt-4">Last Updated: February 2026</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-16 max-w-4xl">
        {/* Table of Contents */}
        <div className="bg-[#141414] rounded-2xl border border-[#D4AF37]/20 p-8 mb-12 shadow-lg">
          <h2 className="text-xl font-bold text-white mb-6 font-heading">Table of Contents</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { num: 1, title: 'Agreement to Terms' },
              { num: 2, title: 'Eligibility & Account Registration' },
              { num: 3, title: 'Products & Services' },
              { num: 4, title: 'Orders & Payment' },
              { num: 5, title: 'Shipping & Delivery' },
              { num: 6, title: 'Returns & Refunds' },
              { num: 7, title: 'Intellectual Property' },
              { num: 8, title: 'User Conduct' },
              { num: 9, title: 'Privacy & Data Protection' },
              { num: 10, title: 'Disclaimer of Warranties' },
              { num: 11, title: 'Limitation of Liability' },
              { num: 12, title: 'Indemnification' },
              { num: 13, title: 'Governing Law' },
              { num: 14, title: 'Changes to Terms' },
              { num: 15, title: 'Contact Information' },
            ].map(item => (
              <a key={item.num} href={`#section-${item.num}`} className="flex items-center gap-3 text-[#5C554A] hover:text-[#5D6B55] font-body transition-colors">
                <span className="w-6 h-6 rounded-full bg-[#5D6B55]/10 flex items-center justify-center text-xs font-bold text-[#5D6B55]">{item.num}</span>
                {item.title}
              </a>
            ))}
          </div>
        </div>

        {/* Section 1 */}
        <section id="section-1" className="mb-12">
          <h2 className="text-2xl font-bold text-[#2C2824] mb-6 font-heading flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#5D6B55]/10 flex items-center justify-center">
              <FileText className="h-5 w-5 text-[#5D6B55]" />
            </div>
            1. Agreement to Terms
          </h2>
          <div className="bg-white rounded-2xl border border-[#E8E4DE] p-8 shadow-sm space-y-4 text-[#5C554A] font-body">
            <p>
              Welcome to {settings?.business_name || 'BOXVERRA'} ("Company," "we," "us," or "our"). These Terms and Conditions ("Terms") govern your access to and use of our website and all related services, features, content, and applications offered by us (collectively, the "Services").
            </p>
            <p>
              By accessing or using our Services, you agree to be bound by these Terms. If you do not agree to all the terms and conditions of this agreement, you may not access or use the Services.
            </p>
            <p>
              <strong>IMPORTANT:</strong> These Terms contain a binding arbitration clause and class action waiver that affect your legal rights. Please read Section 13 carefully.
            </p>
          </div>
        </section>

        {/* Section 2 */}
        <section id="section-2" className="mb-12">
          <h2 className="text-2xl font-bold text-[#2C2824] mb-6 font-heading flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#5D6B55]/10 flex items-center justify-center">
              <Users className="h-5 w-5 text-[#5D6B55]" />
            </div>
            2. Eligibility & Account Registration
          </h2>
          <div className="bg-white rounded-2xl border border-[#E8E4DE] p-8 shadow-sm space-y-6 text-[#5C554A] font-body">
            <div>
              <h3 className="font-bold text-[#2C2824] text-lg mb-3 font-heading">2.1 Eligibility Requirements</h3>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>You must be at least 18 years of age to use our Services</li>
                <li>You must be a legal resident of the United States</li>
                <li>You must have the legal capacity to enter into a binding agreement</li>
                <li>You must not have been previously suspended or removed from our Services</li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-[#2C2824] text-lg mb-3 font-heading">2.2 Account Registration</h3>
              <p className="mb-3">When you create an account with us, you must provide accurate, complete, and current information. You are responsible for:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Maintaining the confidentiality of your account credentials</li>
                <li>All activities that occur under your account</li>
                <li>Notifying us immediately of any unauthorized use of your account</li>
                <li>Ensuring your contact information remains current and accurate</li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-[#2C2824] text-lg mb-3 font-heading">2.3 Account Termination</h3>
              <p>We reserve the right to suspend or terminate your account at any time, without prior notice, for any reason, including but not limited to violation of these Terms.</p>
            </div>
          </div>
        </section>

        {/* Section 3 */}
        <section id="section-3" className="mb-12">
          <h2 className="text-2xl font-bold text-[#2C2824] mb-6 font-heading flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#5D6B55]/10 flex items-center justify-center">
              <Globe className="h-5 w-5 text-[#5D6B55]" />
            </div>
            3. Products & Services
          </h2>
          <div className="bg-white rounded-2xl border border-[#E8E4DE] p-8 shadow-sm space-y-6 text-[#5C554A] font-body">
            <div>
              <h3 className="font-bold text-[#2C2824] text-lg mb-3 font-heading">3.1 Product Information</h3>
              <p className="mb-3">We strive to display our products as accurately as possible. However:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Colors may vary slightly due to monitor settings and photography</li>
                <li>Product dimensions are approximate and may vary slightly</li>
                <li>Product descriptions are for informational purposes only</li>
                <li>We do not warrant that product descriptions are error-free</li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-[#2C2824] text-lg mb-3 font-heading">3.2 Product Availability</h3>
              <p>All products are subject to availability. We reserve the right to discontinue any product at any time without notice. We do not guarantee that all products displayed on the Site are available for purchase.</p>
            </div>
            <div>
              <h3 className="font-bold text-[#2C2824] text-lg mb-3 font-heading">3.3 Pricing</h3>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>All prices are listed in US Dollars (USD)</li>
                <li>Prices are subject to change without notice</li>
                <li>We reserve the right to correct any pricing errors</li>
                <li>Promotional prices are valid for the specified promotional period only</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Section 4 */}
        <section id="section-4" className="mb-12">
          <h2 className="text-2xl font-bold text-[#2C2824] mb-6 font-heading flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#5D6B55]/10 flex items-center justify-center">
              <CreditCard className="h-5 w-5 text-[#5D6B55]" />
            </div>
            4. Orders & Payment
          </h2>
          <div className="bg-white rounded-2xl border border-[#E8E4DE] p-8 shadow-sm space-y-6 text-[#5C554A] font-body">
            <div>
              <h3 className="font-bold text-[#2C2824] text-lg mb-3 font-heading">4.1 Order Placement</h3>
              <p className="mb-3">By placing an order, you are making an offer to purchase. This offer is subject to acceptance by us. An order is accepted when we send you an order confirmation email. We reserve the right to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Refuse or cancel any order for any reason</li>
                <li>Limit quantities available for purchase</li>
                <li>Verify your identity before processing orders</li>
                <li>Cancel orders if fraud or unauthorized activity is suspected</li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-[#2C2824] text-lg mb-3 font-heading">4.2 Payment Methods</h3>
              <p className="mb-3">We accept the following payment methods:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Major credit cards (Visa, MasterCard, American Express, Discover)</li>
                <li>Debit cards with Visa or MasterCard logo</li>
                <li>PayPal</li>
                <li>Apple Pay and Google Pay</li>
                <li>Store credit and gift cards</li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-[#2C2824] text-lg mb-3 font-heading">4.3 Payment Processing</h3>
              <p>Payment is processed at the time of order placement. By providing payment information, you represent that you are authorized to use the payment method and authorize us to charge the full order amount, including applicable taxes and shipping.</p>
            </div>
            <div>
              <h3 className="font-bold text-[#2C2824] text-lg mb-3 font-heading">4.4 Sales Tax</h3>
              <p>Applicable sales tax will be calculated and added to your order based on your shipping address, in accordance with state and local tax laws.</p>
            </div>
          </div>
        </section>

        {/* Section 5 */}
        <section id="section-5" className="mb-12">
          <h2 className="text-2xl font-bold text-[#2C2824] mb-6 font-heading">5. Shipping & Delivery</h2>
          <div className="bg-white rounded-2xl border border-[#E8E4DE] p-8 shadow-sm text-[#5C554A] font-body">
            <p className="mb-4">Shipping and delivery are governed by our Shipping Policy, which is incorporated into these Terms by reference. Key points include:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>We ship to addresses within the United States (including Alaska and Hawaii)</li>
              <li>Delivery times are estimates and not guaranteed</li>
              <li>Risk of loss passes to you upon delivery to the carrier</li>
              <li>You are responsible for providing accurate shipping information</li>
              <li>Additional fees may apply to certain delivery locations</li>
            </ul>
            <p className="mt-4">Please refer to our <a href="/shipping-policy" className="text-[#5D6B55] hover:underline font-semibold">Shipping Policy</a> for complete details.</p>
          </div>
        </section>

        {/* Section 6 */}
        <section id="section-6" className="mb-12">
          <h2 className="text-2xl font-bold text-[#2C2824] mb-6 font-heading">6. Returns & Refunds</h2>
          <div className="bg-white rounded-2xl border border-[#E8E4DE] p-8 shadow-sm text-[#5C554A] font-body">
            <p className="mb-4">Returns and refunds are governed by our Return Policy, which is incorporated into these Terms by reference. Key points include:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>30-day return window for most items</li>
              <li>Items must be in original condition with tags attached</li>
              <li>Certain items are non-returnable (personalized items, used products)</li>
              <li>Refunds are processed to the original payment method</li>
              <li>We reserve the right to refuse returns that do not meet our criteria</li>
            </ul>
            <p className="mt-4">Please refer to our <a href="/return-policy" className="text-[#5D6B55] hover:underline font-semibold">Return Policy</a> for complete details.</p>
          </div>
        </section>

        {/* Section 7 */}
        <section id="section-7" className="mb-12">
          <h2 className="text-2xl font-bold text-[#2C2824] mb-6 font-heading flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#5D6B55]/10 flex items-center justify-center">
              <Lock className="h-5 w-5 text-[#5D6B55]" />
            </div>
            7. Intellectual Property
          </h2>
          <div className="bg-white rounded-2xl border border-[#E8E4DE] p-8 shadow-sm space-y-6 text-[#5C554A] font-body">
            <div>
              <h3 className="font-bold text-[#2C2824] text-lg mb-3 font-heading">7.1 Our Intellectual Property</h3>
              <p>All content on this Site, including but not limited to text, graphics, logos, images, photographs, audio clips, video clips, digital downloads, data compilations, software, and the compilation thereof (collectively, "Content"), is the exclusive property of BOXVERRA or its licensors and is protected by United States and international copyright, trademark, and other intellectual property laws.</p>
            </div>
            <div>
              <h3 className="font-bold text-[#2C2824] text-lg mb-3 font-heading">7.2 Limited License</h3>
              <p className="mb-3">We grant you a limited, non-exclusive, non-transferable license to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Access and view the Content for personal, non-commercial use</li>
                <li>Download one copy of materials for personal, non-commercial use</li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-[#2C2824] text-lg mb-3 font-heading">7.3 Prohibited Uses</h3>
              <p className="mb-3">You may not:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Modify, copy, distribute, transmit, display, perform, reproduce, or create derivative works from the Content</li>
                <li>Use any Content for commercial purposes without our prior written consent</li>
                <li>Remove any copyright, trademark, or other proprietary notices</li>
                <li>Use any data mining, robots, or similar data gathering methods</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Section 8 */}
        <section id="section-8" className="mb-12">
          <h2 className="text-2xl font-bold text-[#2C2824] mb-6 font-heading flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#5D6B55]/10 flex items-center justify-center">
              <Shield className="h-5 w-5 text-[#5D6B55]" />
            </div>
            8. User Conduct
          </h2>
          <div className="bg-white rounded-2xl border border-[#E8E4DE] p-8 shadow-sm text-[#5C554A] font-body">
            <p className="mb-4">When using our Services, you agree NOT to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Violate any applicable laws, regulations, or third-party rights</li>
              <li>Provide false, misleading, or inaccurate information</li>
              <li>Upload or transmit viruses, malware, or other harmful code</li>
              <li>Attempt to gain unauthorized access to our systems or networks</li>
              <li>Interfere with or disrupt the integrity or performance of the Services</li>
              <li>Use the Services for any illegal or unauthorized purpose</li>
              <li>Harass, abuse, or harm other users or our employees</li>
              <li>Engage in any activity that could damage our reputation or goodwill</li>
              <li>Use automated systems (bots, scripts) to access the Services</li>
              <li>Circumvent any security measures or content protection</li>
            </ul>
          </div>
        </section>

        {/* Section 9 */}
        <section id="section-9" className="mb-12">
          <h2 className="text-2xl font-bold text-[#2C2824] mb-6 font-heading">9. Privacy & Data Protection</h2>
          <div className="bg-white rounded-2xl border border-[#E8E4DE] p-8 shadow-sm text-[#5C554A] font-body">
            <p className="mb-4">Your privacy is important to us. Our Privacy Policy describes how we collect, use, and protect your personal information. By using our Services, you consent to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>The collection and processing of your personal data as described in our Privacy Policy</li>
              <li>Receiving marketing communications (you can opt out at any time)</li>
              <li>The use of cookies and similar tracking technologies</li>
              <li>The transfer of your data to service providers who assist us in operating our business</li>
            </ul>
          </div>
        </section>

        {/* Section 10 */}
        <section id="section-10" className="mb-12">
          <h2 className="text-2xl font-bold text-[#2C2824] mb-6 font-heading flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#D4A574]/20 flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-[#D4A574]" />
            </div>
            10. Disclaimer of Warranties
          </h2>
          <div className="bg-[#FEF3E7] rounded-2xl border border-[#D4A574]/30 p-8 shadow-sm text-[#5C554A] font-body">
            <p className="mb-4 font-semibold uppercase text-sm text-[#D4A574]">Important Notice</p>
            <p className="mb-4">
              THE SERVICES AND ALL PRODUCTS ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
              <li>IMPLIED WARRANTIES OF MERCHANTABILITY</li>
              <li>FITNESS FOR A PARTICULAR PURPOSE</li>
              <li>NON-INFRINGEMENT</li>
              <li>ACCURACY, COMPLETENESS, OR RELIABILITY OF CONTENT</li>
            </ul>
            <p>
              We do not warrant that the Services will be uninterrupted, error-free, secure, or free of viruses or other harmful components.
            </p>
          </div>
        </section>

        {/* Section 11 */}
        <section id="section-11" className="mb-12">
          <h2 className="text-2xl font-bold text-[#2C2824] mb-6 font-heading flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#D4A574]/20 flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-[#D4A574]" />
            </div>
            11. Limitation of Liability
          </h2>
          <div className="bg-[#FEF3E7] rounded-2xl border border-[#D4A574]/30 p-8 shadow-sm text-[#5C554A] font-body">
            <p className="mb-4">TO THE FULLEST EXTENT PERMITTED BY LAW, SERENITY HOME SHALL NOT BE LIABLE FOR:</p>
            <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
              <li>Any indirect, incidental, special, consequential, or punitive damages</li>
              <li>Any loss of profits, revenue, data, or goodwill</li>
              <li>Any damages arising from your use or inability to use the Services</li>
              <li>Any damages arising from unauthorized access to your account</li>
            </ul>
            <p className="mb-4">
              IN NO EVENT SHALL OUR TOTAL LIABILITY EXCEED THE GREATER OF: (A) THE AMOUNT YOU PAID FOR THE PRODUCT GIVING RISE TO THE CLAIM; OR (B) ONE HUNDRED DOLLARS ($100).
            </p>
            <p className="text-sm">
              Some jurisdictions do not allow the exclusion or limitation of liability for consequential or incidental damages, so the above limitations may not apply to you.
            </p>
          </div>
        </section>

        {/* Section 12 */}
        <section id="section-12" className="mb-12">
          <h2 className="text-2xl font-bold text-[#2C2824] mb-6 font-heading">12. Indemnification</h2>
          <div className="bg-white rounded-2xl border border-[#E8E4DE] p-8 shadow-sm text-[#5C554A] font-body">
            <p>
              You agree to indemnify, defend, and hold harmless BOXVERRA, its officers, directors, employees, agents, licensors, and suppliers from and against any claims, liabilities, damages, judgments, awards, losses, costs, expenses, or fees (including reasonable attorneys' fees) arising out of or relating to your violation of these Terms or your use of the Services.
            </p>
          </div>
        </section>

        {/* Section 13 */}
        <section id="section-13" className="mb-12">
          <h2 className="text-2xl font-bold text-[#2C2824] mb-6 font-heading flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#5D6B55]/10 flex items-center justify-center">
              <Gavel className="h-5 w-5 text-[#5D6B55]" />
            </div>
            13. Governing Law & Dispute Resolution
          </h2>
          <div className="bg-white rounded-2xl border border-[#E8E4DE] p-8 shadow-sm space-y-6 text-[#5C554A] font-body">
            <div>
              <h3 className="font-bold text-[#2C2824] text-lg mb-3 font-heading">13.1 Governing Law</h3>
              <p>These Terms shall be governed by and construed in accordance with the laws of the State of California, without regard to its conflict of law provisions.</p>
            </div>
            <div>
              <h3 className="font-bold text-[#2C2824] text-lg mb-3 font-heading">13.2 Arbitration Agreement</h3>
              <p className="mb-3">Any dispute arising from or relating to these Terms or your use of the Services shall be resolved through binding arbitration administered by the American Arbitration Association under its Consumer Arbitration Rules.</p>
              <p>You and BOXVERRA each waive the right to a jury trial and the right to participate in a class action or representative proceeding.</p>
            </div>
            <div>
              <h3 className="font-bold text-[#2C2824] text-lg mb-3 font-heading">13.3 Exceptions</h3>
              <p>Notwithstanding the above, either party may seek injunctive relief in any court of competent jurisdiction to prevent the actual or threatened infringement of intellectual property rights.</p>
            </div>
          </div>
        </section>

        {/* Section 14 */}
        <section id="section-14" className="mb-12">
          <h2 className="text-2xl font-bold text-[#2C2824] mb-6 font-heading">14. Changes to Terms</h2>
          <div className="bg-white rounded-2xl border border-[#E8E4DE] p-8 shadow-sm text-[#5C554A] font-body">
            <p className="mb-4">We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting to the Site. Your continued use of the Services after any changes indicates your acceptance of the new Terms.</p>
            <p>We will notify registered users of material changes via email or through a notice on the Site. We encourage you to review these Terms periodically.</p>
          </div>
        </section>

        {/* Section 15 - Contact */}
        <section id="section-15" className="bg-gradient-to-r from-[#5D6B55] to-[#4A5A45] text-white p-8 rounded-2xl shadow-xl">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
              <MessageSquare className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-2xl font-bold font-heading mb-4">15. Contact Information</h2>
              <p className="text-white/90 font-body mb-4">
                If you have any questions about these Terms & Conditions, please contact us:
              </p>
              <div className="space-y-2 text-white/80 font-body">
                <p><strong>Email:</strong> {settings.legal_email}</p>
                <p><strong>Address:</strong> {settings.business_legal_name}<br />{settings.address.street}<br />{settings.address.city}, {settings.address.state} {settings.address.zip}</p>
                <p><strong>Phone:</strong> {settings.phone}</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default TermsConditions;
